import { BudgetRule } from '@frontend/budget/types';
import { AsyncComponent, Spinner } from '@frontend/components/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { ModalType, useModals } from '@frontend/modal';
import { ProductType } from '@frontend/product/types';
import type { Dispatch } from 'react';
import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';

import useBudgetAvailabilityCard, { BudgetAvailabilityBudgetRow, BudgetAvailabilityCardProps } from './budget-availability-card.controller';

const NO_LIMIT_THRESHOLD = 100000;
const BUDGET_RULES_MODAL_ID = 'user-budget-availability-rules-modal';
const ProductSelectV2 = lazy(() => import('@frontend/product/select').then((module) => ({ default: module.ProductSelectV2 })));

interface RulesModalState {
    budgetName: string;
    rules: BudgetRule[];
}

const Quantity = (props: { value: number }) => {
    if (props.value > NO_LIMIT_THRESHOLD) {
        return (
            <FormattedMessage
                id='platform.user.modal.budget-availability.no-limit'
                description='Quantity label shown when a budget has no practical limit'
                defaultMessage='No limit'
            />
        );
    }

    return props.value;
};

const BudgetRuleRow = (props: { rule: BudgetRule }) => (
    <div className='border-border-default flex flex-col gap-2 border-t py-3 first:border-t-0'>
        <div className='text-foreground-default text-sm font-semibold'>{props.rule.name}</div>
        <div className='text-brand-muted flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium'>
            <span>
                <FormattedMessage
                    id='platform.user.modal.budget-availability.rule-quantity'
                    description='Budget rule quantity label in the user budget availability details'
                    defaultMessage='Limit: {quantity}'
                    values={{ quantity: props.rule.quantity }}
                />
            </span>
            <span>
                <FormattedMessage
                    id='platform.user.modal.budget-availability.rule-scope'
                    description='Budget rule scope label in the user budget availability rules modal'
                    defaultMessage='Scope: {scope}'
                    values={{ scope: props.rule.scope }}
                />
            </span>
            <span>
                <FormattedMessage
                    id='platform.user.modal.budget-availability.rule-target'
                    description='Budget rule target label in the user budget availability rules modal'
                    defaultMessage='Target: {target}'
                    values={{ target: props.rule.target }}
                />
            </span>
            {props.rule.period && (
                <span>
                    <FormattedMessage
                        id='platform.user.modal.budget-availability.rule-period'
                        description='Budget rule period label in the user budget availability details'
                        defaultMessage='Period: {period}'
                        values={{ period: props.rule.period }}
                    />
                </span>
            )}
            {props.rule.every && (
                <span>
                    <FormattedMessage
                        id='platform.user.modal.budget-availability.rule-every'
                        description='Budget rule recurrence label in the user budget availability rules modal'
                        defaultMessage='Every: {every}'
                        values={{ every: props.rule.every }}
                    />
                </span>
            )}
        </div>
    </div>
);

const BudgetRulesModal = (props: { state: RulesModalState; onClose: () => void }) => (
    <div className='modal bg-background-default flex h-full flex-col gap-6 p-6'>
        <div className='modal-header flex items-start justify-between px-0 py-0'>
            <div className='flex flex-col gap-1'>
                <div className='text-foreground-default text-3xl font-semibold'>
                    <FormattedMessage
                        id='platform.user.modal.budget-availability.rules-modal-title'
                        description='Title for the budget rules modal for one budget'
                        defaultMessage='Applicable rules'
                    />
                </div>
                <div className='text-brand-muted text-sm font-medium'>{props.state.budgetName}</div>
            </div>
            <button
                type='button'
                onClick={props.onClose}
                className='bg-background-surface hover:bg-brand-light border-border-default flex size-11 cursor-pointer items-center justify-center self-center rounded-md border'>
                <ICONS.BUTTON.CLOSE_2 className='text-brand-muted size-6' />
                <span className='sr-only'>{CommonMessage.BUTTONS.CLOSE}</span>
            </button>
        </div>
        <div className='bg-background-surface border-border-default flex max-h-[60vh] flex-col overflow-hidden rounded-xl border'>
            <div className='overflow-y-auto px-4 py-1'>
                {props.state.rules.map((rule) => (
                    <BudgetRuleRow
                        key={rule.id}
                        rule={rule}
                    />
                ))}
            </div>
        </div>
    </div>
);

const BudgetRulesButton = (props: { row: BudgetAvailabilityBudgetRow; onOpen: Dispatch<RulesModalState> }) => {
    if (props.row.rules.length === 0) {
        return (
            <span className='text-brand-muted text-sm font-medium'>
                <FormattedMessage
                    id='platform.user.modal.budget-availability.no-rules'
                    description='Table cell text shown when a budget has no applicable rules'
                    defaultMessage='No rules'
                />
            </span>
        );
    }

    return (
        <button
            type='button'
            onClick={() => props.onOpen({ budgetName: props.row.budget.name, rules: props.row.rules })}
            className='text-brand-primary hover:border-brand-primary border-border-default inline-flex h-8 items-center rounded-md border px-3 text-sm font-semibold'>
            <FormattedMessage
                id='platform.user.modal.budget-availability.rules-count'
                description='Button label showing how many applicable budget rules can be opened'
                defaultMessage='{count, plural, one {# rule} other {# rules}}'
                values={{ count: props.row.rules.length }}
            />
        </button>
    );
};

export const BudgetAvailabilityCard = (props: BudgetAvailabilityCardProps) => {
    const viewProps = useBudgetAvailabilityCard(props);
    const { openModal, closeModal } = useModals();

    const openRulesModal = (state: RulesModalState) => {
        openModal(
            BUDGET_RULES_MODAL_ID,
            () => (
                <BudgetRulesModal
                    state={state}
                    onClose={() => closeModal(BUDGET_RULES_MODAL_ID)}
                />
            ),
            undefined,
            { type: ModalType.PROMPT, className: 'w-[calc(100%_-_2rem)]! max-w-[640px]! overflow-hidden' }
        );
    };

    return (
        <div className='bg-background-surface border-border-default flex w-full flex-col gap-4 rounded-xl border p-4'>
            <div className='flex flex-col gap-1'>
                <h3 className='text-foreground-default text-base font-semibold'>
                    <FormattedMessage
                        id='platform.user.modal.budget-availability.title'
                        description='Title for the user detail modal budget availability card'
                        defaultMessage='Product budget check'
                    />
                </h3>
                <p className='text-brand-muted text-sm font-medium'>
                    <FormattedMessage
                        id='platform.user.modal.budget-availability.description'
                        description='Short description for checking budget availability for a selected product on the user detail modal'
                        defaultMessage='Select a product to inspect the current budget availability for this user.'
                    />
                </p>
            </div>

            <AsyncComponent>
                <ProductSelectV2
                    id='user-budget-availability-product'
                    label={CommonMessage.OBJECTS.PRODUCT.DEFAULT}
                    isClearable
                    onChangeObject={(product) => {
                        closeModal(BUDGET_RULES_MODAL_ID);
                        viewProps.setSelectedProduct(product);
                    }}
                    queryParams={{
                        account_id: props.accountId,
                        type: [ProductType.CONSUMABLE, ProductType.EQUIPMENT]
                    }}
                />
            </AsyncComponent>

            {viewProps.selectedProduct && (
                <>
                    {viewProps.isLoading && (
                        <div className='flex w-full justify-center py-6'>
                            <Spinner />
                        </div>
                    )}

                    {viewProps.error && <div className='text-status-danger text-sm font-medium'>{viewProps.error}</div>}

                    {!viewProps.isLoading && !viewProps.error && (
                        <>
                            <div className='grid w-full grid-cols-1 gap-2 md:grid-cols-2'>
                                <div className='bg-background-default border-border-default flex flex-col items-start gap-1 rounded-xl border p-3'>
                                    <div className='text-brand-muted text-sm font-medium'>
                                        <FormattedMessage
                                            id='platform.user.modal.budget-availability.left-now'
                                            description='Label for the amount of selected product budget available now'
                                            defaultMessage='Left now'
                                        />
                                    </div>
                                    <div className='text-foreground-default text-xl font-semibold'>
                                        {viewProps.allowance ? <Quantity value={viewProps.allowance.quantity} /> : '/'}
                                    </div>
                                    {viewProps.allowance && (
                                        <div className={`text-xs font-semibold ${viewProps.allowance.allowed ? 'text-status-success' : 'text-status-danger'}`}>
                                            {viewProps.allowance.allowed ? (
                                                <FormattedMessage
                                                    id='platform.user.modal.budget-availability.allowed'
                                                    description='Status label shown when the selected product is allowed by budget'
                                                    defaultMessage='Allowed'
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id='platform.user.modal.budget-availability.not-allowed'
                                                    description='Status label shown when the selected product is not allowed by budget'
                                                    defaultMessage='Not allowed'
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className='bg-background-default border-border-default flex flex-col items-start gap-1 rounded-xl border p-3'>
                                    <div className='text-brand-muted text-sm font-medium'>
                                        <FormattedMessage
                                            id='platform.user.modal.budget-availability.total-used'
                                            description='Label for the total selected product budget use by the selected user'
                                            defaultMessage='Total used'
                                        />
                                    </div>
                                    <div className='text-foreground-default text-xl font-semibold'>{viewProps.totalUsed}</div>
                                </div>
                            </div>

                            {viewProps.allowance ? (
                                <div className='border-border-default overflow-x-auto rounded-xl border'>
                                    <table className='w-full min-w-[620px] text-left text-sm'>
                                        <thead className='bg-background-default text-brand-muted border-border-default border-b text-xs font-semibold uppercase'>
                                            <tr>
                                                <th className='px-4 py-3'>
                                                    <FormattedMessage
                                                        id='platform.user.modal.budget-availability.budget-column'
                                                        description='Column header for the budget name in the user budget availability table'
                                                        defaultMessage='Budget'
                                                    />
                                                </th>
                                                <th className='px-4 py-3'>
                                                    <FormattedMessage
                                                        id='platform.user.modal.budget-availability.left-column'
                                                        description='Column header for remaining quantity in the user budget availability table'
                                                        defaultMessage='Left'
                                                    />
                                                </th>
                                                <th className='px-4 py-3'>
                                                    <FormattedMessage
                                                        id='platform.user.modal.budget-availability.used-column'
                                                        description='Column header for used quantity in the user budget availability table'
                                                        defaultMessage='Used'
                                                    />
                                                </th>
                                                <th className='px-4 py-3'>
                                                    <FormattedMessage
                                                        id='platform.user.modal.budget-availability.rules-column'
                                                        description='Column header for applicable rules in the user budget availability table'
                                                        defaultMessage='Rules'
                                                    />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewProps.budgetRows.length > 0 ? (
                                                viewProps.budgetRows.map((row) => (
                                                    <tr
                                                        key={row.budget.id}
                                                        className='border-border-default border-b last:border-b-0'>
                                                        <td className='text-foreground-default px-4 py-3 font-medium'>{row.budget.name}</td>
                                                        <td className='text-foreground-default px-4 py-3 font-semibold'>
                                                            <Quantity value={row.quantity} />
                                                        </td>
                                                        <td className='text-brand-muted px-4 py-3 font-semibold'>{row.used}</td>
                                                        <td className='px-4 py-3'>
                                                            <BudgetRulesButton
                                                                row={row}
                                                                onOpen={openRulesModal}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={4}
                                                        className='text-brand-muted px-4 py-6 text-center text-sm font-medium'>
                                                        <FormattedMessage
                                                            id='platform.user.modal.budget-availability.no-budget-breakdown'
                                                            description='Empty state shown when no per-budget availability breakdown exists'
                                                            defaultMessage='No budget breakdown available.'
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className='text-brand-muted bg-background-default border-border-default rounded-xl border p-4 text-sm font-medium'>
                                    <FormattedMessage
                                        id='platform.user.modal.budget-availability.no-budget-information'
                                        description='Empty state shown when the analyzer returns no budget information for the selected product'
                                        defaultMessage='No budget information available for this product.'
                                    />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};
